import { Item } from '@sitecore-content-sdk/nextjs';
import { PureHeaderPrimaryLinkBar, PureHeaderSectionLink, SectionLinkList } from './types';
import { Navigation } from '@generated/Navigation/Header.model';

export type HeaderLanguageLink = { text?: string; locale: string };

const getLinkTarget = (linkValue: unknown): string | undefined =>
  (linkValue as { target?: string } | null)?.target || undefined;

export const getHeaderLanguageLinks = (
  supportedLanguages: Navigation.Header.Language[] = []
): HeaderLanguageLink[] =>
  supportedLanguages.reduce<HeaderLanguageLink[]>((links, language) => {
    const text = language.fields?.linkText?.value?.trim();
    const locale =
      language.fields?.locale?.name?.trim() ?? language.fields?.locale?.displayName?.trim();

    if (locale) {
      links.push({ text, locale });
    }

    return links;
  }, []);

export const shouldShowHeaderLanguageRow = (
  hideLanguageToggle: boolean | string | undefined,
  languageLinks: HeaderLanguageLink[]
) =>
  !(hideLanguageToggle === true || String(hideLanguageToggle) === '1') && languageLinks.length > 0;

export const transformToPureHeaderPrimaryLinkBar = (primaryLinks: Item[]) => {
  const primaryLinkBar: PureHeaderPrimaryLinkBar[] = [];
  primaryLinks?.forEach(
    (link: Item & Navigation.Header.PrimaryLinks & Navigation.Header.SimplePrimaryLinks) => {
      const linkType =
        (link.fields?.headerLinkUrl?.value?.linktype as 'internal' | 'external') || 'internal';
      const linkTarget = getLinkTarget(link.fields?.headerLinkUrl?.value);
      const quickLinks = link?.fields?.quickLinks as Item & Navigation.Header.QuickLinks;
      const hideQuickLinks = link.fields?.hideQuickLinks?.value === true;
      const sectionLinkBar: PureHeaderSectionLink[] = [];
      link.fields?.sectionLinks?.map((sectionLink: Item & Navigation.Header.SectionLinks) => {
        const sectionLinkListData: SectionLinkList[] = [];
        sectionLink.fields?.sectionLinkList?.map(
          (sectionLinkList: Item & Navigation.Header.HeaderLink) => {
            sectionLinkListData.push({
              sectionLinkUrl: sectionLinkList.fields.headerLinkUrl?.value?.href || '',
              sectionLinkTarget: getLinkTarget(sectionLinkList.fields.headerLinkUrl?.value),
              sectionLinkLabel: sectionLinkList.fields.headerLinkText?.value || '',
            });
          }
        );

        sectionLinkBar.push({
          sectionLinkList: sectionLinkListData,
          sectionLinkLabel: sectionLink.fields.sectionHeaderText?.value || '',
          sectionLinkUrl: sectionLink.fields.sectionHeaderUrl?.value.href || '',
          sectionLinkTarget: getLinkTarget(sectionLink.fields.sectionHeaderUrl?.value),
          sectionLinksPerColumn: sectionLink.fields.linksPerColumn?.value
            ? parseInt(sectionLink.fields.linksPerColumn?.value)
            : undefined,
        });
      });
      if (link?.fields?.isNavDropDownMenuLink?.value === true) {
        primaryLinkBar.push({
          primaryLinkLabel: link.fields?.headerLinkText?.value || '',
          primaryLinkUrl: link.fields?.headerLinkUrl?.value?.href || '',
          primaryLinkTarget: linkTarget,
          linkType,
          isNavDropDownMenuLink: true,
          primaryLinks:
            link?.fields?.primaryLinks?.map(
              (primaryLink: Item & Navigation.Header.SimplePrimaryLinks) => ({
                headerLinkText: primaryLink.fields?.headerLinkText?.value || '',
                headerLinkUrl: primaryLink.fields?.headerLinkUrl?.value?.href || '',
                headerLinkTarget: getLinkTarget(primaryLink.fields?.headerLinkUrl?.value),
                linkType:
                  (primaryLink.fields?.headerLinkUrl?.value?.linktype as 'internal' | 'external') ||
                  'internal',
              })
            ) || [],
        });
      } else {
        primaryLinkBar.push({
          sectionLinks: sectionLinkBar || [],
          primaryLinkLabel: link.fields?.headerLinkText?.value || '',
          primaryLinkUrl: link.fields?.headerLinkUrl?.value?.href || '',
          primaryLinkTarget: linkTarget,
          hideQuickLinks,
          quickLinksSection: hideQuickLinks
            ? undefined
            : {
                quickLinksSectionViewAllTitle: quickLinks?.fields?.viewAllLinkText?.value || '',
                quickLinksSectionViewAllUrl: quickLinks?.fields?.viewAllLinkUrl?.value?.href || '',
                quickLinksSectionViewAllTarget: getLinkTarget(
                  quickLinks?.fields?.viewAllLinkUrl?.value
                ),
                quickLinkSectionTitle: quickLinks?.fields?.overrideSectionTitle?.value || '',
                quickLinkSectionUrls:
                  quickLinks?.fields?.quickLinkList?.map(
                    (quickLink: Item & Navigation.Header.HeaderLink) => ({
                      quickLinkSectionUrl: quickLink.fields?.headerLinkUrl?.value?.href || '',
                      quickLinkSectionTarget: getLinkTarget(quickLink.fields?.headerLinkUrl?.value),
                      quickLinkSectionLabel: quickLink.fields.headerLinkText?.value || '',
                    })
                  ) || [],
              },
          linkType,
        });
      }
    }
  );
  return primaryLinkBar;
};
// Generate a unique ID for submenu based on section link data
export const generateSubmenuId = (
  sectionLink: PureHeaderSectionLink,
  primaryLinkBarIndex: number,
  sectionLinkIndex: number
): string => {
  const label = sectionLink.sectionLinkLabel?.toLowerCase().replace(/[^a-z0-9]/g, '-') || 'submenu';
  return `submenu-${label}-${primaryLinkBarIndex}-${sectionLinkIndex}`;
};
